import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';

import { ApiError, AuthService, BaseForm, BaseFormService, ConfigService, MessageService } from '../../../core';
import { ConfigEmpresa, isTipoMercadoMarca } from '../../configuracoes/app/empresa';
import { Usuario } from '../usuario';
import { usuarioPerfis, usuarioPerfilRepresentante, isPerfilExpedicao, usuarioPerfilAdministrador } from '../usuario-perfil';
import { UsuarioCrudHttpService } from '../services';
import { Representante } from '../../pessoa/representante';
import { UsuarioAlteraSenhaModalComponent } from '../altera-senha-modal';
import { UsuarioExpedicaoComponent } from '../expedicao';
import { UsuarioMarcaComponent } from '../marca';
import { UsuarioLinhaComponent } from '../linha';
import { UsuarioEmailAdministrativoFormComponent } from '../email/administrativo/form';

@Component({
    selector: 'sgt-usuario-form',
    templateUrl: './usuario-form.component.html',
    styleUrls: ['./usuario-form.component.less']
})
export class UsuarioFormComponent implements BaseForm<Usuario>, OnInit {
    configEmpresa: ConfigEmpresa;
    entity: Usuario;
    editMode: boolean;
    @ViewChild('form') form: NgForm;

    perfis = usuarioPerfis;
    @ViewChild('usuarioAlteraSenhaModal') usuarioAlteraSenhaModal: UsuarioAlteraSenhaModalComponent;


    // Expedição
    @ViewChild('usuarioExpedicaoCadastro') usuarioExpedicaoCadastro: UsuarioExpedicaoComponent;
    // Marcas
    @ViewChild('marcaCadastro') marcaCadastro: UsuarioMarcaComponent;
    // Linhas
    @ViewChild('linhaCadastro') linhaCadastro: UsuarioLinhaComponent;
    // E-mails Administrativos
    @ViewChild('emailAministrativoForm') emailAministrativoForm: UsuarioEmailAdministrativoFormComponent;
    // Unidades de negócios
    // @ViewChild('unidadeNegocioCadastro') unidadeNegocioCadastro: UsuarioUnidadeNegocioComponent;

    constructor(private activatedRoute: ActivatedRoute,
                private authService: AuthService,
                private configService: ConfigService,
                private messageService: MessageService,
                private baseFormService: BaseFormService,
                private usuarioHttpCrudService: UsuarioCrudHttpService) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();

        this.baseFormService.getIdParam(this.activatedRoute).then(id => {
            if (!id) {
                this.insert();
            } else {
                this.edit(id);
            }
        });
    }

    /** Verifica se o Tipo de Mercado da empresa é por Marca */
    isTipoMercadoMarca(): boolean {
        if (!this.configEmpresa) {
            return false;
        }
        return isTipoMercadoMarca(this.configEmpresa.tipoMercado);
    }

    load(id: number) {
        this.usuarioHttpCrudService.load(id)
            .subscribe(
                entity => this.entity = entity,
                (error: ApiError) => {
                    this.messageService.error(error.message, 'Erro ao carregar o usuário', error);
                    this.cancel();
                }
            );
    }

    insert() {
        this.editMode = false;
        this.entity = new Usuario();
        this.entity.idEmpresa = this.authService.getIdEmpresa();
    }

    edit(id: number) {
        this.editMode = true;
        this.load(id);
    }

    /** Valida se pode realizar o save */
    private validateSave(): boolean {
        // Marcas
        if (this.isTipoMercadoMarca() && !this.marcaCadastro.isValid()) {
            this.baseFormService.showTabForm('#tabMarcas');
            return false;
        }
        // Linhas
        if (this.linhaCadastro && !this.linhaCadastro.isValid()) {
            this.baseFormService.showTabForm('#tabLinhas');
            return false;
        }
        // E-mails Administrativos
        if (this.emailAministrativoForm && !this.emailAministrativoForm.isValid()) {
            this.baseFormService.showTabForm('#tabEmails');
            return false;
        }
        // Unidades de negócios
        // if (!this.unidadeNegocioCadastro?.isValid()) {
        //     this.baseFormService.showTabForm('#tabUnidadesNegocios');
        //     return false;
        // }

        return true;
    }

    private beforeSave() {
        // Se não for um usuário do perfil de representante, setamos para null os dados do representante
        if (!this.isPerfilRepresentante()) {
            this.entity.idRepresentante = null;
            this.entity.representante = null;
        }

        // Se for um usuário de perfil Expedição, então resolvemos a listagem de usuários da expedição
        if (this.isPerfilExpedicao()) {
            this.usuarioExpedicaoCadastro.resolve();
        } else {
            this.entity.usuariosExpedicao = []; // Se não for um perfil de expedição, então limpamos a listagem de usuários da expedição
        }

        // Marcas
        if (this.isTipoMercadoMarca() && this.marcaCadastro) {
            this.marcaCadastro.resolve();
        }
        // Linhas
        if (this.linhaCadastro) {
            this.linhaCadastro.resolve();
        }
        // E-mails Administrativos
        if (this.emailAministrativoForm) {
            this.emailAministrativoForm.resolve();
        }
       // Unidades de negócios
    //    this.unidadeNegocioCadastro?.resolve();
    }

    save() {
        if (this.validateSave()) {
            this.beforeSave();
            this.baseFormService.save<Usuario>(this.entity, this.form, this.editMode, this.usuarioHttpCrudService)
                .then(entity => {
                    this.messageService.success('', 'Usuário salvo com sucesso');
                    this.afterSave(entity);
                })
                .catch(error => {});
        }
    }

    afterSave(entity: Usuario) {
        this.cancel();
    }

    cancel() {
        this.baseFormService.cancel(this.activatedRoute);
    }

    /** Ao alterar o perfil do usuário */
    onChangePerfil(perfil: number) {
        this.entity.perfil = perfil;
        // Se estiver inserindo setamos os seus acessos
        if (!this.editMode) {
            this.entity.setAcessos(this.entity.perfil);
        }
    }

    /** Verifica se o perfil do usuário selecionado é o perfil de administrador */
    isPerfilAdministrador(): boolean {
        if (!this.entity || !this.entity.perfil) {
            return false;
        }
        return this.entity.perfil === usuarioPerfilAdministrador.id;
    }

    /** Verifica se o perfil do usuário selecionado é o perfil de representante */
    isPerfilRepresentante(): boolean {
        if (!this.entity || !this.entity.perfil) {
            return false;
        }
        return this.entity.perfil === usuarioPerfilRepresentante.id;
    }

    /** Ao alterar o representante no typeahead */
    onTypeaheadChangeRepresentante(representante: Representante) {
        if (representante) {
            this.entity.idRepresentante = representante.id;
        } else {
            this.entity.idRepresentante = null;
        }
    }

    /** Abre o modal para alterar a senha do usuário */
    openAlterarSenhaModal() {
        this.usuarioAlteraSenhaModal.open(this.entity.id, this.entity.nome);
    }

    /** Expedição */

    /** Verifica se o perfil atual do usuário é Expedição */
    isPerfilExpedicao() {
        if (!this.entity) {
            return false;
        }
        return isPerfilExpedicao(this.entity.perfil);
    }
}
